<template>
   <!-- footer start -->
  <footer class="m-footer">
    <section class="m-footer__banner" v-show="banners.length > 0">
      <h2 class="sr-only">
        <!-- add text -->
        external links
      </h2>
      <div class="m-banner">
        <div class="m-banner__slide" 
          @mouseenter="canScroll && stopLoop()"
          @mouseleave="canScroll && restartLoop()"
          @touchstart="canScroll && stopLoop()"
          @touchend="canScroll && restartLoop()"
          @touchcancel="canScroll && restartLoop()"
          :style="{
            height: `${rowHeight ? rowHeight * (banners.length > bannerInRow ? 2 : 1) : 0}px`,
            overflow: 'hidden'
          }"
        >
          <div class="m-banner__item" :style="{
            transform: `translateY(${
              canScroll 
                ? (translateY !== null ? translateY : -rowHeight)
                : 0}px)`
          }">
            <div class="cx-row--halfgutter -j-center m-banner__main" style="visibility: hidden;">
              <template v-for="(b, idx) in prevRow" >
                <keep-alive :key="idx">
                  <banner 
                    :link="b.link"
                    :title="b.title"
                    :image="b.image"
                    tabindex="-1"
                  ></banner>
                </keep-alive>
              </template>
            </div>

            <div ref="main-row" class="cx-row--halfgutter -j-center m-banner__main">
              <template v-for="(b, idx) in banners.concat(banners.length > bannerInRow ? banners : []).slice(bannerIndex, bannerIndex + bannerInRow)" >
                <keep-alive :key="idx">
                   <banner 
                      :link="b.link"
                      :title="b.title"
                      :image="b.image"
                      updateHeight
                    ></banner>
                </keep-alive>
              </template>
            </div>

            <div class="cx-row--halfgutter -j-center m-banner__main">
              <template v-for="(b, idx) in banners.concat(canScroll ? banners : []).slice(bannerIndex + bannerInRow, bannerIndex + bannerInRow * 2)" >
                <keep-alive :key="idx">
                   <banner 
                      :link="b.link"
                      :title="b.title"
                      :image="b.image"
                      updateHeight
                    ></banner>
                </keep-alive>
              </template>
            </div>

            <div class="cx-row--halfgutter -j-center m-banner__main" v-show="srolling">
              <template v-for="(b, idx) in nextRow" >
                <keep-alive :key="idx">
                  <banner 
                    :link="b.link"
                    :title="b.title"
                    :image="b.image"
                  ></banner>
                </keep-alive>
              </template>
            </div>
          </div>        
        </div>          
        <div class="m-banner__nav cx-t02" v-if="canScroll">
          <a href="#" role="button" :aria-label="isPaused ? $t('action.startScroll') : $t('action.pauseScroll')" class="m-banner__arrow" @click.prevent="toggleLoopPause">
            <i v-show="isPaused" class="fas fa-play"></i>
            <i v-show="!isPaused" class="fas fa-pause"></i>
          </a>
          <a href="#" role="button" :aria-label="$t('common.previousPage')" class="m-banner__arrow" @click.prevent="() => {restartLoop(); goUp();}">
            <i class="fas fa-chevron-up"></i>
          </a>
          <a href="#" role="button" :aria-label="$t('common.nextPage')" class="m-banner__arrow" @click.prevent="() => {restartLoop(); goDown();}">
            <i class="fas fa-chevron-down"></i>
          </a>            
        </div>   
      </div>    
    </section>
    <section class="cx-sec m-footer__main">
      <div class="cx-wrap--page m-wrap--page">
        <div class="cx-row--nogutter">
          <div class="cx-col-lg-6 m-footer__contact">

            <h2 class="cx-t01 m-footer__subtitle">
              <router-link-localed
                class="m-footer__logoLink m-footer__logoLink--mobile"
                :to="{name: `Home_${$i18n.locale}`}">
                <cx-img
                  :alt="$t('footer.contactOffice')"
                  :imgSrc="'/static/logo/mgto.svg'"
                  class="m-footer__logo m-footer__logo--mobile"
                  svg
                  :imgHolder="false"
                ></cx-img>
              </router-link-localed>
              {{$t('footer.contactOffice')}}
            </h2>
            <div class="cx-row--flat">
              <div class="cx-col">
                <router-link-localed
                  :to="{name: `Home_${$i18n.locale}`}">
                  <cx-img
                    :alt="$t('footer.contactOffice')"
                    :imgSrc="'/static/logo/mgto.svg'"
                    class="m-footer__logo m-footer__logo--desktop"
                    svg
                    :imgHolder="false"
                  ></cx-img>
                </router-link-localed>
              </div>
          
              <div class="cx-col">
                <div>
                  <table class="cx-table--borderless m-footer__table">
                    <tbody>
                      <tr>
                        <th>{{$t('common.address')}}</th>
                        <td v-html="$t('footer.contactAddress')"></td>
                      </tr>
                      <tr>
                        <th>{{$t('common.email')}}</th>
                        <td>
                          <template v-for="(email, idx) in contactEmails">
                            <a :key="email"
                              :href="`mailto:${email}`"
                            >{{email}}</a><template v-if="idx + 1 < contactEmails.length">, </template>
                          </template>
                        </td>
                      </tr>
                      <tr>
                        <th>{{$t('common.tel')}}</th>
                        <td dir="ltr">
                          <a :href="`tel:${$t('footer.contactTel').replace(/\s+/g, '')}`"
                          >{{$t('footer.contactTel')}}</a>
                        </td>
                      </tr>
                      <tr v-if="$te('footer.contactFax') && $t('footer.contactFax')">
                        <th>{{$t('common.fax')}}</th>
                        <td dir="ltr">{{$t('footer.contactFax')}}</td>
                      </tr>
                      <tr>
                        <th>{{$t('common.tourismHotline')}}</th>
                        <td dir="ltr">
                          <a :href="`tel:${$t('footer.contactHotline').replace(/\s+/g, '')}`"
                          >{{$t('footer.contactHotline')}}</a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          </div>
          <div class="cx-col-lg-6 m-footer__social">
            <h2 class="cx-t01 m-footer__subtitle">
              {{$t("footer.stayConnected")}}
            </h2>
            <div class="cx-row">
              <div v-if="false" class="cx-col-sm-6">
                <component class="m-footer__btn"
                  :is="''"
                  v-bind="linkProps($t('footer.eNewsLink'))">
                  <div class="cx-row--flat -v-center">
                    <div class="cx-col">
                      <div class="cx-media--icon">
                        <div class="cx-media__graphics m-media__graphics"><i class="fas fa-bullhorn"></i></div>
                      </div>
                    </div>
                    <div class="cx-col m-footer__action">
                      <p class="cx-t10 m-footer__action-subhead">
                        {{$t("footer.eNewsCaption")}}
                      </p>
                      <h3 class="cx-t20">
                        {{$t("footer.eNews")}}
                      </h3>
                    </div>
                  </div>
                </component>
              </div>
              <div class="cx-col-sm-6">
                <component class="m-footer__btn"
                  :is="''"
                  v-bind="linkProps($t('footer.appLink'))">
                  <div class="cx-row--flat -v-center">
                    <div class="cx-col">
                      <div class="cx-media--icon">
                        <div class="cx-media__graphics m-media__graphics">
                          <i class="fas fa-mobile-alt"></i>
                        </div>
                      </div>
                    </div>
                    <div class="cx-col m-footer__action"> 
                      <p class="cx-t10 m-footer__action-subhead">
                        {{$t("footer.appCaption")}}
                      </p>
                      <h3 class="cx-t20">
                        {{$t("footer.app")}}
                      </h3>
                    </div>
                  </div>  
                </component>
              </div>
            </div>
            <div>
              <ul class="cx-list--sm m-footer__sociallist">
                <social-media-subscription-button 
                  v-for="(s, i) in subscriptions"
                  :key="`${s.type}-${$i18n.locale}`"
                  :type="s.type"
                  :subscriptions="s.subscriptions"
                />
              </ul>
            </div>
          </div>
        </div>
        <div class="cx-row--line m-footer__submenu">
          <nav class="cx-footer__nav">
            <ul class="cx-list cx-list--sep">
              <li class="cx-list__item">
                <router-link-localed
                  to="about-us"
                >{{$t("footer.aboutUs")}}</router-link-localed>  
              </li>
              <li class="cx-list__item">
                <router-link-localed
                  to="contact-us"
                >{{$t("footer.contactUs")}}</router-link-localed>  
              </li>
              <li class="cx-list__item">
                <router-link-localed
                  to="terms-and-conditions"
                >{{$t("footer.term")}}</router-link-localed>  
              </li>
              <li class="cx-list__item">
                <router-link-localed
                  to="privacy-statement"
                >{{$t("footer.privacyStatement")}}</router-link-localed>  
              </li>
              
              <li class="cx-list__item">
                <a :href="$t('footer.performancePledgeLink')"
                  rel="noopener noreferrer" target="_blank"
                >{{$t("footer.performancePledge")}}</a>
              </li>
            </ul>
          </nav>
          <div class="cx-footer__copyright m-footer__copyright" 
            v-html="$t('footer.copyright', {year: getCurrentYear()})"
          ></div>
        </div>
      </div>
    </section>
    <a class="m-footer__ribbon" :href="$t('footer.gastronomyLink')" target="_blank" :title="$t('footer.gastronomyTitle')">
      <cx-img
        :alt="$t('footer.gastronomyTitle')"
        :imgSrc="'/static/logo/macao_city_of_gastronomy.svg'"
        class="m-footer__gm"
        svg
        :ignoreBnw="false"
        :imgHolder="false"
      ></cx-img>
      <cx-img
        tag="div"
        :imgSrc="'/static/banner/footerbanner.jpg'"
        class="m-footer__gm-bg"
        :class="{ 'bnw-gray': blackAndWhite }"
        :imgHolder="false"
      ></cx-img>      
    </a>    
  </footer>
</template>

<script>
import TWEEN from 'tween.js'
import axios from 'axios';
import { mapState } from 'vuex';
import getMacauDateTime from '@/utils/getMacauDateTime'
import SocialMediaSubscriptionButton from '@/components/SocialMediaSubscriptionButton';
import MainFooterBanner from '@/components/MainFooterBanner';

export default {
  name: 'main-footer',
  uit() {
    return [
      ['action', ['startScroll', 'pauseScroll']],
      ['common', [
        'address',
        'email',
        'tel',
        'fax',
        'tourismHotline',
        'nextPage',
        'previousPage',
      ]],
      ['footer', [
        'contactOffice',
        'contactAddress',
        'contactEmail',
        'contactTel',
        'contactFax',
        'contactHotline',
        'stayConnected',
        'eNews',
        'eNewsLink',
        'eNewsCaption',
        'app',
        'appLink',
        'appCaption',
        'sitemap',
        'aboutUs',
        'contactUs',
        'term',
        'privacyStatement',
        'performancePledge',
        'performancePledgeLink',
        'copyright',
        'gastronomyLink',
        'gastronomyTitle'
      ]]
    ];
  },
  data() {
    return {
      isPaused: false,
      timer: null,
      rowHeight: 0,
      bannerInRow: 4,
      bannerIndex: 0,
      translateY: null,
      srolling: false,
    }
  },
  components: {
    SocialMediaSubscriptionButton,
    'banner': MainFooterBanner,
  },
  mounted() {
    window.addEventListener('resize', this.updateRowHeight);
    this.bannerInRow = this.isMobileView ? 2 : 4;
    this.startLoop();
  },
  beforeDestroy() {
    this.stopLoop();
    window.addEventListener('resize', this.updateRowHeight);
  },
  computed: {
    ...mapState({
      isMobileView: state => state.ui.isMobileView,
      blackAndWhite: state => state.ui.blackAndWhite,
    }),
    contactEmails: function() {
      return this.$t('footer.contactEmail').split(',').map(m => m.trim());
    },
    subscriptions: function() {
      let raw = this.$store.getters['socialmedia/getSubscription'](this.$i18n.locale);

      const results = [];

      raw.forEach(({type, name, link}) => {
        let i = results.findIndex(r => r.type === type);
        if (i === -1) {
          results.push({ type, subscriptions: [] });
          i = results.length - 1;
        }
        results[i].subscriptions.push({ name, link })
      });

      return results;
    },
    canScroll: function() {
      return this.banners.length > this.bannerInRow * 2;
    },
    banners: function() {
      if (!this.$i18n) return []; // The compoented is decalared
      let currentPath = this.$route.path.split('/').slice(2);
      if (currentPath[currentPath.length - 1] == "") currentPath.pop();
      currentPath = currentPath.join('/');
      currentPath = (currentPath.length > 0 ? '/' : '') + currentPath;
      // TODO: filter
      const banners = this.$store.getters['banner/getFooter'](this.$i18n.locale)
        .filter(b => {
          return b.showOn.some(({url, exact}) => {
            return exact
              ? url == currentPath
              : currentPath.startsWith(url)
          })
        });
      return banners;
    },
    prevRow: function() {
      // get prev banners
      const totalBanner = this.banners.length;
      if (totalBanner <= this.bannerInRow * 2) return [];

      const result = [];
      for (let i = 1; i < this.bannerInRow + 1; i ++) {
        let index = this.bannerIndex - i;
        index = index + (index < 0 ? totalBanner : 0)
        result.push(this.banners[index]);
      }
      
      return result.reverse();
    },
    nextRow: function() {
      // get prev banners
      const totalBanner = this.banners.length;
      if (totalBanner <= this.bannerInRow * 2) return [];

      const result = [];
      const base = this.bannerIndex + 2 * this.bannerInRow - 1;
      for (let i = 1; i < this.bannerInRow + 1; i ++) {
        let index = (base + i) % totalBanner;
        result.push(this.banners[index]);
      }

      return result;
    }
  },
  methods: {
    getCurrentYear() {
      return getMacauDateTime().getFullYear();
    },
    go: function(to, nextIndex) {
      var vm = this;
      if (this.srolling || !this.canScroll) return;
      this.stopLoop();
      this.srolling = true;

      var config = { x: -this.rowHeight }
      var tween = new TWEEN.Tween(config)
        .to({ x: to}, 300)
        .onUpdate(() => {
          vm.translateY = config.x;
        })
        .onComplete(() => {
          vm.bannerIndex = nextIndex;
          vm.translateY = -this.rowHeight;
          vm.srolling = false;
          vm.startLoop();
        });

      tween.start();
      animate();
      function animate() {
        if (vm.srolling) requestAnimationFrame(animate);
        TWEEN.update();
      }
    },
    goUp: function() {
      let nextIndex = this.bannerIndex - this.bannerInRow;
      nextIndex = nextIndex + (nextIndex < 0 ? this.banners.length : 0);

      this.go(0, nextIndex);
    },
    goDown: function() {
      let nextIndex = this.canScroll ? this.bannerIndex + this.bannerInRow : 0;
      nextIndex = nextIndex - (nextIndex >= this.banners.length ? this.banners.length : 0);

      this.go(-(this.rowHeight * 2), nextIndex);
    },
    updateRowHeight: function() {
      this.bannerInRow = this.isMobileView ? 2 : 4;
      this.$nextTick(() => {
        if (!(this.$refs && this.$refs['main-row'])) return;

        setTimeout(() => {
          if (!this.canScroll) {
            this.bannerIndex = 0;
          }
          if (!(this.$refs && this.$refs['main-row'])) return;
          const currentRowHeight = parseFloat(getComputedStyle(this.$refs['main-row']).height.replace('px', ''), 10);
          if (this.rowHeight != currentRowHeight) {
            this.rowHeight = currentRowHeight;
            this.translateY = -currentRowHeight;
          }
        }, 100);
      })
    },
    startLoop: function() {
      if (this.timer !== null) return;
      this.timer = this.createLoop();
    },
    stopLoop: function() {
      if (this.timer == null) return;
      clearInterval(this.timer);
      this.timer = null;
    },
    restartLoop: function() {
      this.stopLoop();
      this.startLoop();
    },
    createLoop: function() {
      return setInterval(this.goDown, 3000);
    },

    toggleLoopPause: function() {
      if (this.isPaused) {
        this.startLoop();
        this.isPaused = false;
      } else {
        this.stopLoop();
        this.isPaused = true;
      }
    }
  },
  watch: {
    isMobileView: function(isMobileView) {
      this.bannerInRow = isMobileView ? 2 : 4;
      this.$nextTick(this.updateRowHeight);
    },
    banners: {
      handler: function() {
        this.updateRowHeight();
      },
      immediate: true,
      deep: true
    }
  }
}
</script>

<style lang="scss">
  .m-footer{
    padding-top: $space-lg;
    padding-bottom: $space-lg;
    background-color: $color-black;
    // color: $color-midgrey;
    color: #D6DEE1; // A11y
    position: relative;
    // gm banner
    margin-bottom: 70px;
    @media(min-width: $breakpoint-md){
      margin-bottom: 100px;
    }
    &:before {
      content: "";
      position: absolute;
      display: block;
      box-shadow: 0 0 10px black;
      width: 100%;
      height: 10px;
      bottom: 0;
      left: 0;
      z-index: -1;
    }
    &:after {
      content: "";
      width: 100%;
      display: block;
      position: sticky;
      bottom: 0;
      left: 0;
      z-index: -2;
      pointer-events: none;
      height: 70px;
      margin-bottom: calc(-#{$space-lg} - 70px); /* 2rem = footer padding-bottom */
      @media(min-width: $breakpoint-md){       
        height: 100px;
        margin-bottom: calc(-#{$space-lg} - 100px); /* 2rem = footer padding-bottom */
      }      
    }
    &:hover::after {
      color: rgb(239, 239, 234);
    }  
   
    a{
      color: inherit;
      display: inline-block;
      &:hover, &:not([href]):not([tabindex]):hover, &:not([href]):not([tabindex]):focus{
        color: $color-white;
        text-decoration: none;
      }
    } 
    &__ribbon{
      position: relative;
      position: sticky;
      bottom: 0;
      display: block !important;
      text-align: center;   
      height: 70px;
      margin-bottom: -70px;
      @supports (position: sticky) or (position: -webkit-sticky) {
        &{
          z-index: -2;
        }
      }
      @media(min-width: $breakpoint-md){       
        height: 100px;
        margin-bottom: -100px;
      }      
    }   
    &__gm-bg{
      @include pos-abs-full;
      @include bg-img;
    }
    &__badge{
      background-color: $color-grey;
      width: 50px;
      height: 50px;
      position: absolute;
      padding-right: $space-sm;
      padding-top: $space-sm;
      top: 0;
      right: 0;
      color: $color-grey !important;
      &:hover{
        color: $color-white !important;
      }
      &:after{
        content: "";
        position: absolute;
        right: 0;
        top: 0;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 0 105px 105px 0;
        border-color: transparent currentColor transparent transparent;
        z-index: 1;
      }     
    }
    &__gm{
      position: relative;
      z-index: 2;
      max-height: 100%;
      padding: $space-sm;
    }
    &__banner{
      margin-top: $space-lg;
    }
    &__main{
      padding-top: $space-lg;
      padding-bottom: $space-lg;
    }
    &__flex{
      display: flex;
    }
    &__logo{
      width: 60px;
      height: 60px;    
      display: block;
      background-size: 100%;
      // background-image: url('/static/logo/mgto.svg');
      // html[data-mode="bnw"] &  {
      //   background-image: url('/static/logo/mgto-bnw.svg');
      // }
      @media(max-width:767px){
        width: 40px;
        height: 40px;
      }
     }
     &__logo--desktop{
       @media(max-width:767px){
         display: none;
       }
     }
     &__logo--mobile{
       display: none;
       @media(max-width:767px){
         display: block;
       }
     }
     &__logoLink--mobile{
       display: none;
       @media(max-width:767px){
         display: inline-flex;
       }
     }
     &__subtitle{
       @extend %style-subtitle;
       padding-bottom: $space-base;
       margin-bottom: 1rem;
       border-bottom: 1px solid lighten($color-black, 5%);
       font-size: 14px !important;
       @media(max-width:767px){
         display: flex;
         align-items: center;
         gap: 10px;
       }
     }
     &__btn{
       margin-bottom: 1rem;
       &:hover{
         color: $color-white;
         text-decoration: none;
       }
     }
     &__table{
      font-size: $font-sm;
       @media(min-width: $breakpoint-lg){
        margin-bottom: $space-base;
      }
      @media(min-width: $breakpoint-md){
        // width: calc(100% - #{$space-sm});
      }
       tr{
         td{
           vertical-align: top;
           padding-left: $int-space-base;
           @media(min-width:$breakpoint-sm){
             padding-right: $int-space-sm;
          }
         }
         th{
           vertical-align: top;
            // min-width: 150px;
            white-space: nowrap;
            @media(max-width:767px){
              white-space: normal;
              // min-width: 60px;
            }
         }
       }
     }
     &__contact{
        padding-bottom: 1rem;
        border-bottom: 1px solid lighten($color-black, 5%);
        @media(max-width:$breakpoint-lg){
         
         order: 2;
        }
     }
     &__social{
        padding-bottom: 1rem;
        border-bottom: 1px solid lighten($color-black, 5%);
        @media(max-width:$breakpoint-lg){
          margin-bottom: 2em;
          border-bottom: 0;
          order: 1;
        }        
     }
      &__action-subhead{
        @extend %style-subtitle;
        margin-bottom: $int-space-xs;
        font-weight: normal;
      }
      &__action{
        @include set-font(.7);
      }
     &__copyright{
         @media(max-width:767px){
        flex-basis: 100%;
         margin-top: 15px;
        }
     }
     &__submenu{
      font-size: $font-sm;
      @media(max-width:767px){
        flex-wrap: wrap;
      }
     }
     &__sociallist{
       & > li{
         font-size: $font-lg;
       }

       // Override global footer hover color: only tint the icon on hover,
       // not the whole link hit-area.
       .cx-link{
         &:hover, &:focus{
           color: inherit;
         }
       }

       a:hover{
         > .fa-facebook-square,
         > .fa-facebook{
           color:#3b5998;
         }
         > .fa-weibo{
           color:#eb7350;
         }
         > .fa-weixin{
           color:#3bba00;
         }
         > .fa-twitter,
         > .fa-x-twitter{
           color:#1da1f2;
         }
         > .fa-instagram{
           color:#c32aa3;
         }
         > .fa-youtube{
           color:red;
         }
         > .fa-pinterest{
           color:#bd081c;
         }
         > .fa-vk{
           color:#4c75a3;
         }
       }
     }
  }
  .m-banner{
    max-width: 1200px;
    margin: auto;
    overflow: hidden;
    &__main{
      margin-bottom: 0 !important;
    }
    // &__sub{
    //   margin-bottom: -$space-base !important;
    // }
    img{
      max-width: 100%;
      width: 100%;
    }
    &__slide{
      display: flex;
      flex-wrap: nowrap;
    }
    &__item{
    flex: 1 0 100%;
    position: relative;
    }    
    &__nav{
      text-align: right;
    }
    &__arrow{
      width: 35px;
      display: inline-block;
      background-color: lighten($color-black, 3%);
      text-align: center;
      border-radius: $br-base;
      cursor: pointer;
      line-height: 1;
      padding: 3px;
      font-size: 11px;
      &:hover {
        background-color: lighten($color-black, 7%);
      }
      &:active{
        background-color: lighten($color-black, 15%);
      }
      i.fas {
        color: #FFFFFF;
      }
    }
  }
  .m-media__graphics{
    transition: box-shadow .1s ease-in-out;
  }


</style>
